import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock, ShieldCheck, Tag, Sparkles, Star, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { api, track, fileUrl, PHONE_DISPLAY, PHONE_TEL, ADDRESS, MAP_URL } from "@/lib/api";
import { useSettings, DEFAULT_HERO_TITLE, DEFAULT_HERO_SUBTITLE } from "@/context/SettingsContext";

const defaultHeroImg =
  "https://images.unsplash.com/photo-1729556227126-452c221f1ef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings() || {};

  const heroImg = settings?.hero_image ? fileUrl(settings.hero_image) : defaultHeroImg;
  const heroTitle = settings?.hero_title || DEFAULT_HERO_TITLE;
  const heroSubtitle = settings?.hero_subtitle || DEFAULT_HERO_SUBTITLE;

  useEffect(() => {
    track("home");
    api.get("/vehicles").then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, []);

  const featured = vehicles.filter((v) => v.status !== "coming_soon").slice(0, 4);
  const comingSoon = vehicles.filter((v) => v.status === "coming_soon");
  const featuredVehicles = vehicles.filter((v) => v.featured && v.status === "available").slice(0, 4);
  const justArrived = vehicles
    .filter(
      (v) =>
        v.status === "available" &&
        v.show_just_arrived !== false &&
        v.created_at &&
        Date.now() - new Date(v.created_at).getTime() < 14 * 24 * 3600 * 1000
    )
    .slice(0, 8);

  return (
    <div className="min-h-screen pb-16 md:pb-0" data-testid="home-page">
      <Header />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Erskine Auto lot" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <span className="inline-flex items-center gap-2 bg-red-600/90 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
            <Tag size={14} /> Oak Bay, New Brunswick
          </span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight max-w-2xl">
            {heroTitle}
          </h1>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
            {heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/inventory">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold h-12 px-6 rounded-full active:scale-95" data-testid="hero-browse-button">
                Browse Inventory <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Coming Soon strip */}
      {comingSoon.length > 0 && (
        <section className="bg-gradient-to-b from-amber-50 to-white border-b border-amber-100" data-testid="coming-soon-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                <Clock size={14} /> Coming Soon
              </span>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900">
                Coming Soon to the Lot
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {comingSoon.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust badges */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, title: "Trusted Local Dealer", sub: "Serving Southern N.B." },
            { icon: MapPin, title: ADDRESS, sub: "Easy to find on Route 170" },
            { icon: Clock, title: "Mon–Sat 9am–6pm", sub: "By appointment · call ahead" },
          ].map((b) => (
            <div key={b.title} className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                <b.icon size={20} />
              </span>
              <div>
                <p className="font-semibold text-sm text-slate-900">{b.title}</p>
                <p className="text-xs text-slate-500">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured vehicles */}
      {featuredVehicles.length > 0 && (
        <section className="bg-gradient-to-b from-amber-50 to-white border-b border-amber-100" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                <Star size={14} fill="currentColor" /> Featured
              </span>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900">
                Handpicked for You
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredVehicles.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Just Arrived strip */}
      {justArrived.length > 0 && (
        <section className="bg-white border-b border-slate-200" data-testid="just-arrived-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                <Sparkles size={14} /> Just Arrived
              </span>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-900">
                Fresh to the Lot
              </h2>
            </div>
            <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2 snap-x">
              {justArrived.map((v, i) => (
                <div key={v.id} className="min-w-[260px] sm:min-w-[300px] snap-start">
                  <VehicleCard vehicle={v} index={i} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured inventory */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">On the Lot Now</h2>
            <p className="text-slate-500 text-sm mt-1">Fresh arrivals ready for a test drive.</p>
          </div>
          <Link to="/inventory" className="text-red-600 font-semibold text-sm hover:underline hidden sm:flex items-center gap-1" data-testid="home-view-all-link">
            View all <ArrowRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : featured.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No vehicles available right now. Check back soon or give us a call.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        )}

        <div className="mt-8 text-center sm:hidden">
          <Link to="/inventory">
            <Button variant="outline" className="rounded-full font-semibold">View all inventory <ArrowRight size={16} className="ml-2" /></Button>
          </Link>
        </div>
      </section>

      {/* Sales */}
      {["owner", "sales"].some((p) => settings?.[`${p}_photo`] || settings?.[`${p}_name`]) && (
        <section className="bg-white border-t border-slate-200" data-testid="home-sales-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-8">Meet Our Team</h2>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-8 sm:gap-14">
              {["owner", "sales"].filter((p) => settings?.[`${p}_photo`] || settings?.[`${p}_name`]).map((p) => (
                <div key={p} className="flex items-center gap-5" data-testid={`home-${p}-card`}>
                  {settings?.[`${p}_photo`] && (
                    <img
                      src={fileUrl(settings[`${p}_photo`])}
                      alt={settings?.[`${p}_name`] || p}
                      className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-slate-100 shadow-md shrink-0"
                      data-testid={`home-${p}-photo`}
                    />
                  )}
                  <div>
                    {settings?.[`${p}_name`] && (
                      <p className="font-display font-bold text-xl text-slate-900" data-testid={`home-${p}-name`}>{settings[`${p}_name`]}</p>
                    )}
                    {settings?.[`${p}_title`] && (
                      <p className="text-slate-500 mt-1" data-testid={`home-${p}-title`}>{settings[`${p}_title`]}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Map / visit */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Come Visit Us</h2>
            <p className="text-slate-300 mt-2">{ADDRESS} · Call {PHONE_DISPLAY}</p>
          </div>
          <a href={MAP_URL} target="_blank" rel="noreferrer">
            <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold h-12 px-6 rounded-full" data-testid="home-directions-button">
              <MapPin size={18} className="mr-2" /> Get Directions
            </Button>
          </a>
        </div>
      </section>

      <Footer />
      <StickyCallBar />
    </div>
  );
}
